/* eslint-disable react-hooks/exhaustive-deps */
import DirectorNotesButton from "components/DirectorNotesButton";
import FileUpload from "components/FileUpload";
import ImageModal from "components/ImageModal";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import SuspectDetailInfo from "pages/Incident/IncidentDetail/modals/SuspectDetailInfo";
import AppealRequestForm from "pages/InstituteOfCharge/modal/AppealRequestForm";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  addBailHearingDecision,
  addBailRightFeedback,
  editBailRight,
  editBailRight2,
  fetchBailHearingDecisionByBailRightId,
  fetchBailRightById,
} from "services/bailRightService";
import {
  BailHearingDecision,
  BailRight,
  BailRightStatus,
  BailRightType,
  CrimeType,
  CrimeTypeForInvestigationType,
  FEEDBACK_MODEL,
  FILE_URL,
  Roles,
  Suspect,
} from "types";
import { BASE_URL } from "utils/constant";
import { DateView, errorHandlerOnForm, hasRole } from "utils/helper";
import BailRightFeedbackSection from "./fragment/BailRightFeedback";
import DocumentViewerModal from "components/DocumentViewer";
import Loading from "components/Loading";

// Reusable Card Component
const Card: React.FC<{
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}> = ({ children, className = "", onClick }) => (
  <div
    className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${
      onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""
    } ${className}`}
    onClick={onClick}
  >
    {children}
  </div>
);

// Info Item Component
const InfoItem: React.FC<{
  label: string;
  value: React.ReactNode;
  className?: string;
}> = ({ label, value, className = "" }) => (
  <div className={`flex flex-col ${className}`}>
    <dt className="text-sm font-medium text-gray-600 mb-1">{label}</dt>
    <dd className="text-sm font-semibold text-gray-900">{value}</dd>
  </div>
);

// Animated, color-coded countdown badge for a future date (e.g. hearing day).
const HearingCountdown: React.FC<{
  date?: string | null;
  t: any;
}> = ({ date, t }) => {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);
  if (!date) return null;
  const target = new Date(date).getTime();
  if (isNaN(target)) return null;
  const days = Math.ceil((target - now) / 86400000);

  let cls = "bg-emerald-50 text-emerald-700 border-emerald-200";
  let label = `${days} ${t("days_left", { defaultValue: "days left" })}`;
  let urgent = false;
  if (days < 0) {
    cls = "bg-gray-100 text-gray-500 border-gray-200";
    label = `${Math.abs(days)} ${t("days_ago", { defaultValue: "days ago" })}`;
  } else if (days === 0) {
    cls = "bg-red-50 text-red-700 border-red-200";
    label = t("today", { defaultValue: "Today" });
    urgent = true;
  } else if (days <= 3) {
    cls = "bg-red-50 text-red-700 border-red-200";
    urgent = true;
  } else if (days <= 7) {
    cls = "bg-amber-50 text-amber-700 border-amber-200";
  }

  return (
    <span
      className={`relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${cls} ${
        urgent ? "animate-pulse" : ""
      }`}
    >
      {urgent && (
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
        </span>
      )}
      ⏳ {label}
    </span>
  );
};

// Action Button Component
const ActionButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline";
  loading?: boolean;
  className?: string;
  t: (key: string) => string;
}> = ({
  onClick,
  children,
  variant = "primary",
  loading = false,
  className = "",
  t,
}) => {
  const baseClasses =
    "px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2";
  const variants = {
    primary: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
    secondary: "bg-gray-600 text-white hover:bg-gray-700 shadow-sm",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${
        loading ? "opacity-50 cursor-not-allowed" : ""
      } ${className}`}
      onClick={onClick}
      disabled={loading}
    >
      {loading ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          {t("processing")}
        </>
      ) : (
        children
      )}
    </button>
  );
};

function HearingDateWithCountdown({ hearing, t }: any) {
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const appt = hearing?.appointment_date
    ? new Date(hearing.appointment_date)
    : null;
  let countdown = "";
  if (appt && !isNaN(appt.getTime())) {
    const diff = appt.getTime() - now;
    if (diff > 0) {
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      countdown = `${days}d ${hours}h ${minutes}m ${seconds}s`;
    }
  }
  return (
    <div className="text-right">
      <p className="text-sm text-gray-500">{t("hearing_date")}</p>
      <p className="text-sm font-semibold text-gray-900">
        {appt && !isNaN(appt.getTime())
          ? DateView(hearing.appointment_date)
          : t("na")}
      </p>
      {countdown && (
        <p className="text-xs text-blue-600 font-mono">
          {t("countdown")}: {countdown}
        </p>
      )}
    </div>
  );
}

// Helper to check if this is the latest appointment date
function isLatestAppointment(hearing: any, allHearings: any) {
  if (!hearing?.appointment_date) return false;
  const appt = new Date(hearing.appointment_date);
  if (isNaN(appt.getTime())) return false;
  let maxDate = null;
  for (const h of allHearings || []) {
    if (h?.appointment_date) {
      const d = new Date(h.appointment_date);
      if (!isNaN(d.getTime()) && (!maxDate || d > maxDate)) {
        maxDate = d;
      }
    }
  }
  return maxDate && appt.getTime() === maxDate.getTime();
}

// Show only remaining days for the latest appointment date
function HearingDateDaysLeft({ hearing, t, isLatest }: any) {
  if (!isLatest) return null;
  const appt = hearing?.appointment_date
    ? new Date(hearing.appointment_date)
    : null;
  let daysLeft = null;
  if (appt && !isNaN(appt.getTime())) {
    const now = Date.now();
    const diff = appt.getTime() - now;
    if (diff > 0) {
      daysLeft = Math.ceil(diff / (1000 * 60 * 60 * 24));
    }
  }
  return (
    <div className="text-right">
      <p className="text-sm text-gray-500">{t("hearing_date")}</p>
      <p className="text-sm font-semibold text-gray-900">
        {appt && !isNaN(appt.getTime())
          ? DateView(hearing.appointment_date)
          : t("na")}
      </p>
      {typeof daysLeft === "number" && daysLeft >= 0 && (
        <p className="text-xs text-blue-600 font-mono">
          {t("days_left")}: {daysLeft}
        </p>
      )}
    </div>
  );
}

// Status Badge Component
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const getStatusColor = (statusValue: string) => {
    const statusMap: { [key: string]: string } = {
      submitted: "bg-yellow-100 text-yellow-800 border-yellow-200",
      assigned_to_prosecutor: "bg-red-100 text-red-800 border-red-200",
      forwarded_to_coordinator:
        "bg-purple-100 text-purple-800 border-purple-200",
      bail_granted: "bg-green-100 text-green-800 border-green-200",
      bail_rejected: "bg-red-100 text-red-800 border-red-200",
    };
    return (
      statusMap[statusValue] || "bg-gray-100 text-gray-800 border-gray-200"
    );
  };

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
        status,
      )}`}
    >
      {BailRightStatus[status as keyof typeof BailRightStatus] || status}
    </span>
  );
};

// Defendant Card Component
const DefendantCard: React.FC<{
  defendant: any;
  onViewImage: () => void;
  onViewDetails: () => void;
  t: (key: string) => string;
}> = ({ defendant, onViewImage, onViewDetails, t }) => (
  <Card>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <img
          src={defendant?.picture || require("../../images/imageVector.png")}
          alt={t("defendant")}
          className="w-12 h-12 rounded-lg cursor-pointer border border-gray-300"
          onClick={onViewImage}
        />
        <div>
          <h4 className="font-semibold text-gray-900">
            {`${defendant?.first_name} ${defendant?.middle_name} ${defendant?.last_name}`}
          </h4>
          <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
            {defendant?.age && (
              <span>
                {defendant.age} {t("years")}
              </span>
            )}
            {defendant?.education_level && (
              <>
                <span>•</span>
                <span>{defendant.education_level}</span>
              </>
            )}
            {defendant?.nationality && (
              <>
                <span>•</span>
                <span>{defendant.nationality}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <button
        onClick={onViewDetails}
        className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-50 rounded-lg transition-colors"
        title={t("view_defendant_details")}
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      </button>
    </div>
  </Card>
);

// File Download Component
const FileDownload: React.FC<{
  label: string;
  url?: string;
  t: (key: string) => string;
}> = ({ label, url, t }) => (
  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
        <svg
          className="w-5 h-5 text-red-600"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      <span className="font-medium text-gray-900">{label}</span>
    </div>
    {url ? (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
      >
        {t("download")}
      </a>
    ) : (
      <span className="text-gray-500 text-sm">{t("na")}</span>
    )}
  </div>
);

const BailRightDetail: React.FC = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const navigate = useNavigate();

  const [prosecutors, setProsecutors] = React.useState<
    {
      user: { id: string; first_name: string; last_name: string };
    }[]
  >([]);

  const [bailIsLoading, setBailIsLoading] = useState(false);
  const [actionIsLoading, setActionIsLoading] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isSuspectDetailOpen, setIsSuspectDetailOpen] = useState(false);
  const [openAppealForm, setOpenAppealForm] = useState(false);
  const [bailDecision, setBailDecision] = useState<"accept" | "reject" | null>(
    null,
  );

  const [bailRight, setBailRight] = useState<BailRight>();
  const [bailHearingDecisions, setBailHearsingDecisions] = useState<
    BailHearingDecision[]
  >([]);
  // Left-column view: the request, the hearings, or the prosecutor decision.
  const [activeView, setActiveView] = useState<
    "request" | "hearing" | "decision"
  >("request");

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);

  const [prosecutorSummary, setProsecutorSummary] = useState("");
  const [prosecutorDcument, setProsecutorDocument] = useState<File | null>(
    null,
  );
  const [referenceNumber, setReferenceNumber] = useState("");
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentReason, setAppointmentReason] = useState("");
  const [courtHearing, setCourtHearing] = useState("");
  const [courtOrder, setCourtOrder] = useState("");
  const [dailyHearingDetail, setDailyHearingDetail] = useState("");
  const [isFinalDecision, setIsFinalDecision] = useState(false);
  const [finalOutcome, setFinalOutcome] = useState<
    "favored" | "opposed" | "favored_rejected"
  >();
  const [decisionAction, setDecisionAction] = useState<
    "grant" | "not_grant" | "pending" | ""
  >("");
  const [prosecutorFeedback, setProsecutorFeedback] = useState("");
  const [toAppeal, setToAppeal] = useState(false);
  const [bailCourtDecision, setBailCourtDecition] = useState<File | null>(null);
  const [receiptOfPayment, setRecipetOfPayment] = useState<File | null>(null);
  const [feedback, setFeedback] = useState("");
  const [assignProsecutorUserid, setAssignProsecutorUserid] = useState<
    string | null
  >(null);
  const [bailAmount, setBailAmount] = useState<number | string>();
  const [department, setDepartment] = useState<string | null>(null);

  const fetchData = () => {
    if (!id) return;
    setBailIsLoading(true);

    fetchBailRightById(id)
      .then((data) => {
        setBailRight(data);
        setDepartment(data?.crime_type || null);
      })
      .catch((error) => {
        showTooltip(
          errorHandlerOnForm(error) || t("error_fetching_case_details"),
          "error",
        );
      })
      .finally(() => {
        setBailIsLoading(false);
      });
  };

  const fetcHearingData = () => {
    if (bailRight?.uu_id === undefined) return;

    fetchBailHearingDecisionByBailRightId(bailRight?.uu_id)
      .then((data) => {
        setBailHearsingDecisions(data);
      })
      .catch((error) => {
        showTooltip(
          errorHandlerOnForm(error) || t("error_fetching_case_details"),
          "error",
        );
      });
  };

  const fetcProsecutorData = (
    adminId: string | number,
    orgId: string | number,
  ) => {
    fetch(
      `${BASE_URL}prosecutor-police/investigation-files/fetch-employees-by-org/?admin_id=${adminId}&org_id=${orgId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
        },
      },
    )
      .then((response) => response.json())
      .then((data) => {
        setProsecutors(
          data?.filter(
            (d: any) =>
              d?.roles?.includes("Prosecutor") ||
              d?.roles?.includes("ProsecutionCaseProsecutor"),
          ),
        );
      })
      .catch((error) => {
        console.error(t("error_fetching_prosecutors"), error);
      })
      .finally(() => {
        // setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (bailRight?.uu_id) {
      fetcHearingData();
    }
  }, [bailRight]);

  // A coordinator with a pending decision lands on the Prosecutor Decision tab.
  useEffect(() => {
    if (
      isCoordinator &&
      bailRight?.status === "forwarded_to_coordinator" &&
      bailRight?.prosecutor_summary &&
      !bailRight?.reference_number
    ) {
      setActiveView("decision");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bailRight?.uu_id]);

  useEffect(() => {
    if (loginData && isCoordinator) {
      fetcProsecutorData(loginData?.admin_unit, loginData?.org_unit);
    }
  }, [loginData, isCoordinator]);

  useEffect(() => {
    setAssignProsecutorUserid(bailRight?.assign_prosecutor_userid || null);
  }, [bailRight?.assign_prosecutor_userid]);

  const handleAssignProsecutor = (successMessage: string) => {
    if (!assignProsecutorUserid) {
      showTooltip(t("please_select_prosecutor"), "error");
      return;
    }

    setActionIsLoading(true);
    const data = prosecutors?.find(
      (p) => p.user?.id === assignProsecutorUserid,
    );

    editBailRight2({
      uu_id: bailRight?.uu_id,
      assign_prosecutor_userid: assignProsecutorUserid,
      assign_prosecutor: data?.user
        ? `${data.user.first_name} ${data.user.last_name}`
        : "",
      status: "assigned_to_prosecutor",
    })
      .then(() => {
        showTooltip(successMessage, "success");
        fetchData();
        setActionIsLoading(false);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
        setActionIsLoading(false);
      });
  };

  const handleForwardDepartment = (successMessage: string) => {
    if (!department) {
      showTooltip("Please select department", "error");
      return;
    }
    if (department === bailRight?.crime_type) {
      showTooltip("Please select a different department", "error");
      return;
    }

    setActionIsLoading(true);

    editBailRight2({
      uu_id: bailRight?.uu_id,
      crime_type: department || undefined,
    })
      .then(() => {
        showTooltip(successMessage, "success");
        navigate("/bail-right");
        setActionIsLoading(false);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
        setActionIsLoading(false);
      });
  };

  const handleInitialBailDecision = () => {
    if (!bailDecision) return;

    if (!prosecutorSummary) {
      showTooltip(t("please_enter_prosecutor_summary"), "error");
      return;
    }

    setActionIsLoading(true);
    editBailRight({
      uu_id: bailRight?.uu_id,
      prosecutor_summary: prosecutorSummary,
      prosecutor_document: prosecutorDcument,
      status: "forwarded_to_coordinator",
      is_bail_prosecutor_approved: bailDecision === "accept",
    })
      .then(() => {
        showTooltip(
          bailDecision === "accept"
            ? t("bail_accepted")
            : t("successfully_submitted"),
          "success",
        );
        fetchData();
        setBailDecision(null);
        setProsecutorSummary("");
        setProsecutorDocument(null);
        setActionIsLoading(false);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
        setActionIsLoading(false);
      });
  };

  // Render Hearing Decisions
  const renderHearingDecisions = () => {
    if (bailHearingDecisions?.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl border border-gray-200">
          <svg
            className="w-12 h-12 text-gray-300 mx-auto mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 17v-2a4 4 0 018 0v2m-8-4h8"
            />
          </svg>
          <p className="text-lg font-medium">
            {t("no_hearing_decisions_available")}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {bailHearingDecisions
          ?.slice()
          .sort((a, b) => {
            const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
            const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
            return dateB - dateA;
          })
          ?.map((hearing, index) => (
            <Card
              key={index}
              className={`${hearing?.if_final_decision_given ? "border-l-[10px] border-green-700" : ""}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 ${hearing?.if_final_decision_given ? "bg-green-100" : "bg-red-100"} rounded-lg flex items-center justify-center`}
                  >
                    <span
                      className={`${hearing?.if_final_decision_given ? "text-green-600" : "text-red-600"} text-lg`}
                    >
                      ⚖️
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t("hearing_decision")}
                    </h3>
                    <p className="text-gray-500 text-sm">
                      {t("prosecutor")}: {hearing?.assign_prosecutor}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {!hearing?.if_final_decision_given && (
                  <InfoItem
                    label={t("appointment_reason")}
                    value={hearing?.appointment_reason || t("na")}
                  />
                )}
                {!hearing?.if_final_decision_given && (
                  <InfoItem
                    label={t("court_order")}
                    value={hearing?.court_order || t("na")}
                  />
                )}
                {hearing?.if_final_decision_given && (
                  <div className={`flex flex-col`}>
                    <dt className="text-sm font-medium text-gray-600 mb-1">
                      {t("bail_amount")}
                    </dt>
                    <dd className="text-sm font-semibold text-red-900 ">
                      {hearing?.bail_amount || t("na")}
                    </dd>
                  </div>
                )}
                {hearing?.if_final_decision_given && (
                  <InfoItem
                    label={t("bail_stance")}
                    value={
                      hearing?.bail_stance
                        ? {
                            favored: t("favored"),
                            opposed: t("opposed"),
                            favored_rejected: t("favored_rejected"),
                          }[hearing.bail_stance]
                        : t("na")
                    }
                  />
                )}
                {hearing?.if_final_decision_given && (
                  <InfoItem
                    label={t("to_appeal")}
                    value={hearing?.to_appeal ? t("yes") : t("no")}
                  />
                )}
              </div>

              <div className="space-y-4">
                {/* <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    {t("court_hearing")}
                  </h4>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                    {hearing.court_hearing || t("na")}
                  </p>
                </div> */}

                {!hearing?.if_final_decision_given && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">
                      {t("daily_hearing_detail")}
                    </h4>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                      {hearing.daily_hearing_detail || t("na")}
                    </p>
                  </div>
                )}

                {hearing?.if_final_decision_given && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">
                      {t("daily_hearing_detail")}
                    </h4>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                      {hearing.daily_hearing_detail || t("na")}
                    </p>
                  </div>
                )}

                {hearing?.if_final_decision_given &&
                  hearing?.prosecutor_feedback && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">
                        {t("prosecutor_opinion", {
                          defaultValue: "Prosecutor Opinion",
                        })}
                      </h4>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                        {hearing.prosecutor_feedback}
                      </p>
                    </div>
                  )}

                {hearing?.if_final_decision_given && (
                  <DocumentViewerModal
                    documents={(hearing?.hearing_documents || []) as any}
                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.BAIL_RIGHT}`}
                    layout="list"
                    label={t("court_decision_document")}
                    hideEmptyFile={true}
                  />
                )}
              </div>

              {hearing?.receipt_of_payment_details && (
                <div className="mt-4">
                  <FileDownload
                    label={t("receipt_of_payment")}
                    url={hearing.receipt_of_payment_details.url?.toString()}
                    t={t}
                  />
                </div>
              )}
            </Card>
          ))}
      </div>
    );
  };

  // Render Bail Right Information
  // Prosecutor Decision tab: the prosecutor's summary + the coordinator's
  // approve / respond-back action on the same card.
  const renderProsecutorDecision = () => (
    <div className="space-y-6">
      {bailRight?.prosecutor_summary ? (
        <Card>
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-green-600 text-lg">👨‍⚖️</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t("prosecutor_summary")}
                </h3>
                <p className="text-gray-500 text-sm">
                  {t("assigned_prosecutor")}: {bailRight?.assign_prosecutor}
                </p>
              </div>
            </div>
            <StatusBadge status={bailRight?.status || ""} />
          </div>

          <p className="text-gray-700 leading-relaxed mb-4">
            {bailRight?.prosecutor_summary}
          </p>

          <DocumentViewerModal
            documents={
              (bailRight?.documents?.filter(
                (a) =>
                  a?.document_type === "bail_prosecutor_document" ||
                  a?.document_type === "court_bail_application",
              ) || []) as any
            }
            baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.BAIL_RIGHT}`}
            layout="list"
            label={t("prosecutor_document")}
            hideEmptyFile={true}
          />
        </Card>
      ) : (
        <Card>
          <div className="text-center py-8 text-gray-500">
            {t("no_prosecutor_decision_yet", {
              defaultValue: "The prosecutor has not submitted a decision yet.",
            })}
          </div>
        </Card>
      )}

      {/* Coordinator action: respond back or approve & forward to registrar */}
      {isCoordinator &&
        bailRight?.status === "forwarded_to_coordinator" &&
        bailRight?.prosecutor_summary &&
        !bailRight?.reference_number && (
          <Card>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 shrink-0 rounded-lg bg-red-50 flex items-center justify-center text-red-600 text-lg">
                👨‍💼
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900">
                  {t("coordinator_action")}
                </h3>
                <p className="text-xs text-gray-500">
                  {t("coordinator_decision_hint", {
                    defaultValue:
                      "Review the prosecutor's decision, then respond back or approve and forward to the registrar.",
                  })}
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("feedback")}
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  value={feedback}
                  rows={4}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder={t("enter_your_feedback")}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ActionButton
                  onClick={() => {
                    setActionIsLoading(true);
                    editBailRight2({
                      uu_id: bailRight?.uu_id,
                      status: "bail_rejected",
                    })
                      .then(() => {
                        if (feedback && bailRight?.uu_id) {
                          addBailRightFeedback({
                            coordinator_user_id: loginData?.user_id ?? "",
                            coordinator_fullname: loginData?.full_name || "",
                            comment: feedback,
                            decision: "respond_back",
                            bail_right: bailRight?.uu_id ?? "",
                          });
                        }
                        showTooltip(
                          t("successfully_responded_back"),
                          "success",
                        );
                        fetchData();
                      })
                      .catch((error) => {
                        showTooltip(
                          error?.response?.data?.detail ||
                            errorHandlerOnForm(error),
                          "error",
                        );
                      })
                      .finally(() => setActionIsLoading(false));
                  }}
                  variant="outline"
                  loading={actionIsLoading}
                  t={t}
                  className="w-full"
                >
                  ↩️ {t("respond_back")}
                </ActionButton>
                <ActionButton
                  onClick={() => {
                    setActionIsLoading(true);
                    editBailRight2({
                      uu_id: bailRight?.uu_id,
                      status: "forwarded_to_registrar",
                    })
                      .then(() => {
                        if (feedback && bailRight?.uu_id) {
                          addBailRightFeedback({
                            coordinator_user_id: loginData?.user_id ?? "",
                            coordinator_fullname: loginData?.full_name || "",
                            comment: feedback,
                            decision: "approve",
                            bail_right: bailRight?.uu_id ?? "",
                          });
                        }
                        showTooltip(t("successfully_approved"), "success");
                        fetchData();
                      })
                      .catch((error) => {
                        showTooltip(
                          error?.response?.data?.detail ||
                            errorHandlerOnForm(error),
                          "error",
                        );
                      })
                      .finally(() => setActionIsLoading(false));
                  }}
                  loading={actionIsLoading}
                  t={t}
                  className="w-full"
                >
                  ✓ {t("approve_and_forward", { defaultValue: "Approve & Forward" })}
                </ActionButton>
              </div>
            </div>
          </Card>
        )}
    </div>
  );

  const renderBailRightInfo = () => (
    <div className="space-y-6">

      {/* Main Bail Right Information */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {t("bail_right_information")}
            </h2>
            <p className="text-gray-600">
              {t("crime_registration_number")}: #
              {bailRight?.crime_registration_number}
            </p>
          </div>
          <StatusBadge status={bailRight?.status || ""} />
        </div>

        {[
          {
            title: t("case_crime_information", {
              defaultValue: "Case & Crime Information",
            }),
            fields: [
              {
                label: t("bail_type"),
                value: bailRight?.bail_type
                  ? BailRightType[bailRight.bail_type]
                  : "",
              },
              { label: t("bail_amount"), value: bailRight?.bail_amount },
              {
                label: t("crime_registration_number"),
                value: bailRight?.crime_registration_number,
              },
              {
                label: t("incident_registration_number"),
                value: bailRight?.incident_registration_number,
              },
              {
                label: t("prosecutor_registration_number"),
                value: bailRight?.prosecutor_registration_number,
              },
            ],
          },
          {
            title: t("court_information", {
              defaultValue: "Court & Hearing",
            }),
            fields: [
              {
                label: t("court_registration_number"),
                value: bailRight?.court_registration_number,
              },
              { label: t("court_name"), value: bailRight?.court_name },
              {
                label: t("reference_number"),
                value: bailRight?.reference_number,
              },
              {
                label: t("court_open_day"),
                value: bailRight?.court_open_day
                  ? DateView(bailRight.court_open_day)
                  : "",
              },
              {
                label: t("court_hearing_day"),
                value: bailRight?.court_hearing_date ? (
                  <span className="flex items-center gap-2 flex-wrap">
                    {DateView(bailRight.court_hearing_date)}
                    <HearingCountdown
                      date={bailRight.court_hearing_date as any}
                      t={t}
                    />
                  </span>
                ) : (
                  ""
                ),
              },
              {
                label: t("appointment_date"),
                value: bailRight?.appointment_date
                  ? DateView(bailRight.appointment_date)
                  : "",
              },
              {
                label: t("assigned_prosecutor"),
                value: bailRight?.assign_prosecutor,
              },
            ],
          },
        ].map((group: { title: string; fields: any[] }, gi: number) => {
          // Hide empty / N-A fields; skip the whole group if nothing remains.
          const visible = group.fields.filter(
            (f: any) => String(f.value ?? "").trim() !== "",
          );
          if (visible.length === 0) return null;
          return (
            <div key={gi} className="mb-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-1.5 h-5 rounded-full bg-red-600" />
                <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700">
                  {group.title}
                </h3>
                <div className="flex-1 border-t border-gray-100" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visible.map((f: any, i: number) => (
                  <InfoItem key={i} label={f.label} value={f.value} />
                ))}
              </div>
            </div>
          );
        })}

        {String(bailRight?.bail_evidence_description ?? "").trim() !== "" && (
          <div className="mb-6">
            <InfoItem
              label={t("bail_evidence_description")}
              value={bailRight?.bail_evidence_description}
              className="mb-4"
            />
          </div>
        )}

        {/* Suspect (a.k.a. defendant) information — one unified section. */}
        {(() => {
          const card = bailRight?.suspect_info;
          const fullname = bailRight?.suspect_fullname;
          const fields = [
            { label: t("gender"), value: bailRight?.suspect_gender },
            { label: t("age"), value: bailRight?.suspect_age },
            {
              label: t("nationality"),
              value: bailRight?.suspect_nationality,
            },
            {
              label: t("identification_number", {
                defaultValue: "Identification Number",
              }),
              value: bailRight?.suspect_identification_number,
            },
            {
              label: t("current_imprisonment_location", {
                defaultValue: "Current Imprisonment Location",
              }),
              value: bailRight?.current_imprisonment_location,
            },
            {
              label: t("detention_start_date", {
                defaultValue: "Detention Start Date",
              }),
              value: bailRight?.detention_start_date
                ? DateView(bailRight.detention_start_date)
                : "",
            },
            {
              label: t("crime_for_suspected", {
                defaultValue: "Crime For Suspected",
              }),
              value: bailRight?.crime_for_suspected,
            },
          ].filter((f: any) => String(f.value ?? "").trim() !== "");

          return (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t("suspect_information")}
              </h3>
              {card ? (
                <DefendantCard
                  defendant={card}
                  onViewImage={() => setIsImageModalOpen(true)}
                  onViewDetails={() => setIsSuspectDetailOpen(true)}
                  t={t}
                />
              ) : fullname ? (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="font-semibold text-gray-900">{fullname}</p>
                </div>
              ) : null}

              {fields.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
                  {fields.map((f: any, i: number) => (
                    <InfoItem key={i} label={f.label} value={f.value} />
                  ))}
                </div>
              )}

              {!card && !fullname && fields.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  {t("no_defendant_information_available")}
                </div>
              )}
            </div>
          );
        })()}

        {/* Evidence Files */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t("evidence_files")}
          </h3>
          <DocumentViewerModal
            documents={
              (bailRight?.documents?.filter(
                (a) => a?.document_type === "court_bail_application",
              ) || []) as any
            }
            baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.BAIL_RIGHT}`}
            layout="list"
            label={t("court_bail_application_file")}
            hideEmptyFile={true}
          />
        </div>
      </Card>
    </div>
  );

  // Render Action Sections based on user role and bail right status
  const renderActionSections = () => {
    const actionSections = [];

    // Coordinator: Assign Prosecutor
    if (
      isCoordinator &&
      bailRight?.status === "forwarded_to_coordinator" &&
      !bailRight?.prosecutor_summary &&
      !bailRight?.reference_number
    ) {
      actionSections.push(
        <Card key="coordinator-action" className="min-w-[400px]">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 shrink-0 rounded-lg bg-red-50 flex items-center justify-center text-red-600 text-lg">
              👨‍💼
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900">
                {t("coordinator_action")}
              </h3>
              <p className="text-xs text-gray-500">
                {t("coordinator_action_hint", {
                  defaultValue:
                    "Assign a prosecutor, or forward this request to the concerned department.",
                })}
              </p>
            </div>
          </div>

          {/* Assign to prosecutor */}
          <div className="rounded-xl border border-gray-200 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-base">🧑‍⚖️</span>
              <h4 className="text-sm font-semibold text-gray-800">
                {t("assign_to_prosecutor")}
              </h4>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t("select_prosecutor")}
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                value={assignProsecutorUserid || ""}
                onChange={(e) =>
                  setAssignProsecutorUserid(e.target.value || null)
                }
              >
                <option value="">{t("select_prosecutor")}</option>
                {prosecutors.map((p, index) => (
                  <option key={index} value={p.user?.id || ""}>
                    {p.user?.first_name} {p.user?.last_name}
                  </option>
                ))}
              </select>
            </div>
            <ActionButton
              onClick={() => handleAssignProsecutor(t("successfully_assigned"))}
              loading={actionIsLoading}
              t={t}
              className="w-full"
            >
              👤 {t("assign_to_prosecutor")}
            </ActionButton>
          </div>

          {/* OR divider */}
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 border-t border-gray-200" />
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
              {t("or", { defaultValue: "or" })}
            </span>
            <div className="flex-1 border-t border-gray-200" />
          </div>

          {/* Forward to concerned department */}
          <div className="rounded-xl border border-gray-200 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-base">🏢</span>
              <h4 className="text-sm font-semibold text-gray-800">
                {t("forward_to_concerned_department", {
                  defaultValue: "Forward to Concerned Department",
                })}
              </h4>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t("select_department", { defaultValue: "Select Department" })}
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                value={department || ""}
                onChange={(e) => setDepartment(e.target.value || null)}
              >
                <option value="">
                  {t("select_department", { defaultValue: "Select Department" })}
                </option>
                {Object.entries(CrimeTypeForInvestigationType).map(
                  ([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ),
                )}
              </select>
            </div>
            <ActionButton
              onClick={() =>
                handleForwardDepartment(
                  t("successfully_forwarded_to_department", {
                    defaultValue: "Successfully forwarded to department",
                  }),
                )
              }
              loading={actionIsLoading}
              variant="outline"
              t={t}
              className="w-full"
            >
              📤 {t("forward")}
            </ActionButton>
          </div>
        </Card>,
      );
    }

    if (
      isCoordinator &&
      bailRight?.status === "assigned_to_prosecutor" &&
      !bailRight?.reference_number
    ) {
      actionSections.push(
        <Card key="coordinator-reassign" className="min-w-[400px]">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t("coordinator_action")}
          </h3>
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
              <span className="font-medium text-gray-900">
                {t("assigned_prosecutor")}:
              </span>{" "}
              {bailRight?.assign_prosecutor || t("na")}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("select_prosecutor")}
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg bg-white p-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                value={assignProsecutorUserid || ""}
                onChange={(e) =>
                  setAssignProsecutorUserid(e.target.value || null)
                }
              >
                <option value="">{t("select_prosecutor")}</option>
                {prosecutors.map((p, index) => (
                  <option key={index} value={p.user?.id || ""}>
                    {p.user?.first_name} {p.user?.last_name}
                  </option>
                ))}
              </select>
            </div>
            <ActionButton
              onClick={() => handleAssignProsecutor(t("successfully_revised"))}
              loading={actionIsLoading}
              t={t}
            >
              {t("re_assign_prosecutor")}
            </ActionButton>
          </div>
        </Card>,
      );
    }

    // Prosecutor: Initial Submission
    if (
      isProsecutor &&
      !bailRight?.reference_number &&
      (bailRight?.status === "assigned_to_prosecutor" ||
        bailRight?.status === "bail_rejected")
    ) {
      actionSections.push(
        <Card key="prosecutor-initial" className="min-w-[460px]">
          {/* Header */}
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 shrink-0 rounded-lg bg-red-50 flex items-center justify-center text-red-600 text-lg">
              🧑‍⚖️
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900">
                {t("prosecutor_action")}
              </h3>
              <p className="text-xs text-gray-500">
                {t("prosecutor_action_hint", {
                  defaultValue:
                    "Review the bail request and submit your decision to the coordinator.",
                })}
              </p>
            </div>
          </div>

          {/* Decision selector */}
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            {t("your_decision", { defaultValue: "Your Decision" })}
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setBailDecision("accept")}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-colors ${
                bailDecision === "accept"
                  ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              ✓ {t("accept_bail")}
            </button>
            <button
              type="button"
              onClick={() => setBailDecision("reject")}
              className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-colors ${
                bailDecision === "reject"
                  ? "border-red-500 bg-red-50 text-red-700"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              ✕ {t("reject_bail")}
            </button>
          </div>

          {/* Details once a decision is chosen */}
          {bailDecision && (
            <div className="mt-4 space-y-4 pt-4 border-t border-gray-100">
              <div
                className={`flex items-center gap-2 rounded-lg p-3 text-sm font-medium border ${
                  bailDecision === "accept"
                    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                    : "bg-red-50 text-red-800 border-red-200"
                }`}
              >
                <span>{bailDecision === "accept" ? "✓" : "✕"}</span>
                {t("decision", { defaultValue: "Decision" })}:{" "}
                {bailDecision === "accept"
                  ? t("accept_bail")
                  : t("reject_bail")}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("bail_evidence_description")}
                  <span className="text-red-500"> *</span>
                </label>
                <p className="text-xs text-gray-400 mb-2">
                  {t("prosecutor_summary_hint", {
                    defaultValue:
                      "Explain the reasoning behind your decision.",
                  })}
                </p>
                <textarea
                  className="w-full border border-gray-300 rounded-lg bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  value={prosecutorSummary}
                  rows={4}
                  onChange={(e) => setProsecutorSummary(e.target.value)}
                  placeholder={t("enter_detailed_bail_evidence_description")}
                />
              </div>

              <FileUpload
                label={t("prosecutor_document")}
                fileTypes="*"
                required={true}
                onChange={(file) => setProsecutorDocument(file)}
              />

              <div className="flex gap-3">
                <ActionButton
                  onClick={() => {
                    setBailDecision(null);
                    setProsecutorSummary("");
                    setProsecutorDocument(null);
                  }}
                  variant="outline"
                  t={t}
                  className="flex-1"
                >
                  {t("cancel")}
                </ActionButton>
                <ActionButton
                  onClick={handleInitialBailDecision}
                  loading={actionIsLoading}
                  t={t}
                  className="flex-1"
                >
                  📤 {t("submit_to_coordinator")}
                </ActionButton>
              </div>
            </div>
          )}
        </Card>,
      );
    }

    // Coordinator approve / respond-back now lives on the "Prosecutor
    // Decision" tab (renderProsecutorDecision), so it is not pushed here.

    // Registrar: Set Reference Number
    if (
      isRegistrar &&
      bailRight?.status === "forwarded_to_registrar" &&
      !bailRight?.reference_number
    ) {
      actionSections.push(
        <Card key="registrar-reference" className="min-w-[400px]">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t("registrar_action")}
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("reference_number")}
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg bg-white p-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder={t("enter_reference_number")}
              />
            </div>
            <ActionButton
              onClick={() => {
                if (!referenceNumber) {
                  showTooltip(t("please_enter_reference_number"), "error");
                  return;
                }
                setActionIsLoading(true);
                editBailRight2({
                  uu_id: bailRight?.uu_id,
                  reference_number: referenceNumber,
                  status: "assigned_to_prosecutor",
                })
                  .then(() => {
                    showTooltip(t("successfully_updated"), "success");
                    fetchData();
                  })
                  .catch((error) => {
                    showTooltip(
                      error?.response?.data?.detail ||
                        errorHandlerOnForm(error),
                      "error",
                    );
                  })
                  .finally(() => setActionIsLoading(false));
              }}
              loading={actionIsLoading}
              t={t}
            >
              {t("save_reference_number")}
            </ActionButton>
          </div>
        </Card>,
      );
    }

    // Prosecutor: Hearing Decision (Complex Form). Only on the "Show Hearing"
    // tab, once the registrar has set the reference number (so it has one) and
    // no final decision exists yet — regardless of the prosecutor's initial
    // accept/reject stance.
    if (
      activeView === "hearing" &&
      isProsecutor &&
      bailRight?.status === "assigned_to_prosecutor" &&
      bailRight?.reference_number &&
      bailHearingDecisions?.find((a) => a?.if_final_decision_given === true) ===
        undefined
    ) {
      actionSections.push(
        <Card key="prosecutor-hearing" className="min-w-[460px]">
          {/* Header */}
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 shrink-0 rounded-lg bg-red-50 flex items-center justify-center text-red-600 text-lg">
              ⚖️
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900">
                {t("hearing_decision")}
              </h3>
              <p className="text-xs text-gray-500">
                {t("hearing_decision_hint", {
                  defaultValue:
                    "Log a daily hearing report, or record the final bail decision.",
                })}
              </p>
            </div>
          </div>

          {/* Tabs: Daily Report vs Final Decision */}
          <div className="flex w-full p-1 rounded-lg bg-gray-100 mb-4">
            <button
              type="button"
              onClick={() => setIsFinalDecision(false)}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                !isFinalDecision
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              📅 {t("daily_report", { defaultValue: "Daily Report" })}
            </button>
            <button
              type="button"
              onClick={() => setIsFinalDecision(true)}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isFinalDecision
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              🎯 {t("final_decision", { defaultValue: "Final Decision" })}
            </button>
          </div>

          <div className="space-y-4">
            {!isFinalDecision ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("appointment_date")}
                    <span className="text-red-500"> *</span>
                  </label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-lg bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    value={
                      appointmentDate
                        ? new Date(appointmentDate).toISOString().split("T")[0]
                        : ""
                    }
                    min={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setAppointmentDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("appointment_reason")}
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    value={appointmentReason}
                    rows={3}
                    onChange={(e) => setAppointmentReason(e.target.value)}
                    placeholder={t("enter_appointment_reason")}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("court_order")}
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    value={courtOrder}
                    rows={3}
                    onChange={(e) => setCourtOrder(e.target.value)}
                    placeholder={t("enter_court_order_details")}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("daily_hearing_detail")}
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    value={dailyHearingDetail}
                    rows={3}
                    onChange={(e) => setDailyHearingDetail(e.target.value)}
                    placeholder={t("enter_daily_hearing_details")}
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("final_outcome")}
                    <span className="text-red-500"> *</span>
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    value={finalOutcome}
                    onChange={(e) =>
                      setFinalOutcome(
                        e.target.value as
                          | "favored"
                          | "opposed"
                          | "favored_rejected",
                      )
                    }
                  >
                    <option value="">{t("select_final_outcome")}</option>
                    <option value="favored">{t("favored")}</option>
                    <option value="opposed">{t("opposed")}</option>
                    <option value="favored_rejected">
                      {t("favored_rejected")}
                    </option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("decision_action")}
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-lg bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    value={decisionAction}
                    onChange={(e) =>
                      setDecisionAction(
                        e.target.value as
                          | "grant"
                          | "not_grant"
                          | "pending"
                          | "",
                      )
                    }
                  >
                    <option value="">{t("select_decision_action")}</option>
                    <option value="grant">{t("grant")}</option>
                    <option value="not_grant">{t("not_grant")}</option>
                    <option value="pending">{t("pending")}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("bail_amount")}
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-lg bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    value={bailAmount}
                    onChange={(e) => setBailAmount(e.target.value)}
                    placeholder={"10000"}
                  />
                </div>
                <FileUpload
                  label={t("bail_court_decision")}
                  fileTypes="*"
                  required={false}
                  onChange={(file) => setBailCourtDecition(file)}
                />
                <FileUpload
                  label={t("receipt_of_payment")}
                  fileTypes="*"
                  required={false}
                  onChange={(file) => setRecipetOfPayment(file)}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("prosecutor_feedback")}
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    value={prosecutorFeedback}
                    rows={3}
                    onChange={(e) => setProsecutorFeedback(e.target.value)}
                    placeholder={t("enter_prosecutor_feedback")}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("daily_hearing_detail")}
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    value={dailyHearingDetail}
                    rows={3}
                    onChange={(e) => setDailyHearingDetail(e.target.value)}
                    placeholder={t("enter_daily_hearing_details")}
                  />
                </div>
                <label className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm font-medium text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                    checked={toAppeal}
                    onChange={() => setToAppeal(!toAppeal)}
                  />
                  {t("to_appeal")}
                </label>
              </>
            )}

            <ActionButton
              className="w-full"
              onClick={() => {
                setActionIsLoading(true);
                addBailHearingDecision({
                  bail_right: bailRight?.uu_id ?? null,
                  appointment_date: appointmentDate,
                  appointment_reason: appointmentReason,
                  bail_amount: bailAmount,
                  court_hearing: courtHearing,
                  court_order: courtOrder,
                  daily_hearing_detail: dailyHearingDetail,
                  if_final_decision_given: isFinalDecision,
                  bail_stance: isFinalDecision ? finalOutcome : undefined,
                  decision_action:
                    isFinalDecision && decisionAction
                      ? decisionAction
                      : undefined,
                  prosecutor_feedback: isFinalDecision
                    ? prosecutorFeedback
                    : undefined,
                  to_appeal: isFinalDecision ? toAppeal : undefined,
                  receipt_of_payment: isFinalDecision
                    ? receiptOfPayment
                    : undefined,
                  court_decision_document: isFinalDecision
                    ? bailCourtDecision
                    : undefined,
                  assign_prosecutor_userid:
                    loginData?.user_id !== undefined &&
                    loginData?.user_id !== null
                      ? loginData.user_id.toString()
                      : "",
                  assign_prosecutor: loginData?.full_name || "",
                })
                  .then(() => {
                    showTooltip(t("successfully_saved"), "success");
                    fetchData();
                    // Reset form
                    setAppointmentDate("");
                    setAppointmentReason("");
                    setCourtHearing("");
                    setCourtOrder("");
                    setDailyHearingDetail("");
                    setIsFinalDecision(false);
                    setFinalOutcome(undefined);
                    setDecisionAction("");
                    setProsecutorFeedback("");
                    setToAppeal(false);
                    setRecipetOfPayment(null);
                  })
                  .catch((error: any) => {
                    showTooltip(
                      error?.response?.data?.detail ||
                        errorHandlerOnForm(error),
                      "error",
                    );
                  })
                  .finally(() => setActionIsLoading(false));
              }}
              loading={actionIsLoading}
              t={t}
            >
              {t("save_hearing_decision")}
            </ActionButton>
          </div>
        </Card>,
      );
    }

    // Prosecutor: Request Appeal
    if (
      isProsecutor &&
      bailRight?.status === "assigned_to_prosecutor" &&
      bailRight?.reference_number &&
      bailHearingDecisions?.find((a) => a?.if_final_decision_given === true)
    ) {
      actionSections.push(
        <Card key="prosecutor-appeal" className="min-w-[460px]">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t("appeal_action")}
          </h3>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t("do_you_want_to_request_appeal")}
            </p>
            <ActionButton
              onClick={() => {
                setOpenAppealForm(true);
              }}
              variant="outline"
              t={t}
            >
              {t("request_appeal")}
            </ActionButton>
          </div>
        </Card>,
      );
    }

    return actionSections;
  };

  // Show remaining days to the latest appointment date (if in the future)
  function LatestAppointmentDaysLeft({ hearings, t }: any) {
    if (!hearings || hearings.length === 0) return null;
    // Only show if there is NO hearing with if_final_decision_given: true
    if (hearings.some((h: any) => h?.if_final_decision_given === true))
      return null;
    let maxAppt = null;
    for (const h of hearings) {
      if (h?.appointment_date) {
        const d = new Date(h.appointment_date);
        if (!isNaN(d.getTime()) && (!maxAppt || d > maxAppt)) {
          maxAppt = d;
        }
      }
    }
    if (!maxAppt) return null;
    const now = Date.now();
    const diff = maxAppt.getTime() - now;
    if (diff <= 0) return null;
    const daysLeft = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return (
      <span className="ml-2 px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold border border-red-200">
        {t("days_left")}: {daysLeft}
      </span>
    );
  }

  return (
    <div className="lg:h-full flex flex-col bg-gray-50 py-4 relative">
      {((bailRight as any)?.uu_id || (bailRight as any)?.id || id) && (
        <DirectorNotesButton
          model={FEEDBACK_MODEL.bail}
          objectId={
            ((bailRight as any)?.uu_id || (bailRight as any)?.id || id) as any
          }
          role={
            isProsecutor
              ? "prosecutor"
              : isRegistrar
                ? "registrar"
                : undefined
          }
        />
      )}

      <div className="px-4 sm:px-6 lg:px-8 flex flex-col flex-1 lg:min-h-0">
        {bailIsLoading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-red-600 border-t-transparent mb-4"></div>
              <span className="text-lg font-semibold text-red-700">
                <Loading />
              </span>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="shrink-0 relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm mb-6">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-red-700 via-red-600 to-red-500" />
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-5">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-12 h-12 shrink-0 rounded-xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white text-2xl shadow-md shadow-red-200">
                ⚖️
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl font-bold text-gray-900">
                    {t("bail_right_case")}
                  </h1>
                  <LatestAppointmentDaysLeft
                    hearings={bailHearingDecisions}
                    t={t}
                  />
                  {bailRight?.status && (
                    <span className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-red-50 text-red-700 border border-red-200">
                      {BailRightStatus[
                        bailRight.status as keyof typeof BailRightStatus
                      ] || bailRight.status}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-0.5">
                  {t("detailed_view_of_bail_right_proceedings")}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 shrink-0">
              {/* Left-column view tabs */}
              <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-gray-100">
                {[
                  { key: "request" as const, label: `📋 ${t("show_request")}` },
                  ...(bailRight?.reference_number
                    ? [
                        {
                          key: "hearing" as const,
                          label: `⚖️ ${t("show_hearing")}`,
                        },
                      ]
                    : []),
                  ...(bailRight?.prosecutor_summary
                    ? [
                        {
                          key: "decision" as const,
                          label: `🧑‍⚖️ ${t("prosecutor_decision", {
                            defaultValue: "Prosecutor Decision",
                          })}`,
                        },
                      ]
                    : []),
                ].map((tb) => (
                  <button
                    key={tb.key}
                    type="button"
                    onClick={() => setActiveView(tb.key)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      activeView === tb.key
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {tb.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6 lg:flex-1 lg:min-h-0">
          {/* Left Column - Main Content (narrower) */}
          <div className="xl:col-span-3 space-y-6 lg:overflow-y-auto lg:min-h-0 lg:pr-1">
            {activeView === "hearing"
              ? renderHearingDecisions()
              : activeView === "decision"
                ? renderProsecutorDecision()
                : renderBailRightInfo()}
          </div>

          {/* Right Column - Actions + Feedback (both open, wider) */}
          <div className="xl:col-span-2 space-y-6 lg:overflow-y-auto lg:min-h-0 lg:pr-1">
            <div className="space-y-6">{renderActionSections()}</div>
            {activeView !== "hearing" && (
              <Card>
                <h3 className="text-base font-semibold text-gray-900 mb-3">
                  💬 {t("feedback")}
                </h3>
                <BailRightFeedbackSection
                  coordinator_feedbacks={bailRight?.coordinator_feedbacks || []}
                />
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {isImageModalOpen && (
        <ImageModal
          isOpen={isImageModalOpen}
          imageSrc={
            bailRight?.suspect_info?.picture_file ||
            require("../../images/imageVector.png")
          }
          label={`${bailRight?.suspect_info?.first_name} ${bailRight?.suspect_info?.last_name}`}
          onClose={() => setIsImageModalOpen(false)}
        />
      )}

      {isSuspectDetailOpen && (
        <SuspectDetailInfo
          suspect={bailRight?.suspect_info as Suspect}
          isOpen={isSuspectDetailOpen}
          onClose={() => setIsSuspectDetailOpen(false)}
        />
      )}

      {openAppealForm && (
        <AppealRequestForm
          bailRight={bailRight}
          appealType="BAILRIGHT"
          onClose={() => setOpenAppealForm(false)}
        />
      )}
    </div>
  );
};

export default BailRightDetail;
